import moment from "moment";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { Article } from "@domain/lms/article/article";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useIsMobile } from "@utils/hooks";
import { truncString } from "@utils/strings";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { CalendarIconBis, EyeIcon, TimeIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";
import { SubAuthor } from "@ui/components/subAuthor";
import { ArticleThumbnailSmallHeader } from "./articleThumbnailSmallHeader";

interface ArticleThumbnailSmallProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
	displayAuthorAsMe?: boolean;
	forFeed?: boolean;
	small?: boolean;
	isSmall?: boolean;
	forInsight?: boolean;
	isNew?: boolean;
}
export const ArticleThumbnailSmall: React.FC<ArticleThumbnailSmallProps> = ({
	article,
	displayAuthorAsMe,
	forFeed = false,
	small: forceSmall,
	forInsight = false,
	isNew = false,
}) => {
	const { author } = article;
	const isMobile = useIsMobile();
	const displayedAuthor = getDisplayedAuthor(article);
	const subAuthor = displayedAuthor !== author ? author : undefined;
	const smallVersion = forceSmall || isMobile;
	const MAX_TEMPER = 20;
	return (
		<Container>
			<Link to={Path.ReferenceArticle({ id: article.id, slug: article.slug, category: article.category })}>
				{!forFeed && <ArticleThumbnailSmallHeader article={article} isSmall={smallVersion} />}
				<ArtcileContainer forInsight={forInsight}>
					<StyledContainer>
						<StyledSubdiv>
							<div>
								<EyeIcon color={theme.colors.black} />
								<span>{article.views.count}</span>
							</div>
							<div>
								<TimeIcon color={theme.colors.black} />
								<span>{article.duration} min</span>
							</div>
							<div>
								<CalendarIconBis color={theme.colors.black} />
								<span>{moment(article.createdAt).format("D MMM YYYY")}</span>
							</div>
						</StyledSubdiv>
						<ArticleTitle>{truncString(article.title, MAX_TEMPER)}</ArticleTitle>
						{/* <ArticleDescription>{truncString(articleContent, MAX_TEMPER)}</ArticleDescription> */}
						{!isNew ? (
							<BottomInfos>
								<StyledUserView
									unClickable={true}
									user={displayedAuthor}
									alignStart={!!subAuthor}
									renderBottomAccessory={
										subAuthor ? () => <SubAuthor author={subAuthor} displayAuthorAsMe={displayAuthorAsMe} /> : undefined
									}
								/>
							</BottomInfos>
						) : null}
					</StyledContainer>
				</ArtcileContainer>
			</Link>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	border-radius: 13px;
	cursor: pointer;
	overflow: hidden;
`;

const ArtcileContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "forInsight",
})<{ forInsight?: boolean }>`
	display: flex;
	flex-direction: column;
	position: relative;
	background-color: white;
	overflow: hidden;
	border-bottom-left-radius: 13px;
	border-bottom-right-radius: 13px;
	height: 150px;
	${({ forInsight }) => (forInsight ? "border: solid 1px #eaebef" : undefined)};
`;
const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: 20px;
`;

const ArticleTitle = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isFeed",
})<{ isFeed?: boolean }>`
	${({ theme }) => theme.textStyles.desktopSubtitle};
	/* max-height: 50px;
	min-height: 50px; */
	word-break: break-word;
	display: -webkit-box;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-height: 1.3;
	padding-top: 0;
	margin-bottom: 1px;
	font-size: 18px;
	color: #000000;
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	margin-top: 20px;

	/* @media (max-width: 900px) {
		max-height: 39px;
		margin: 4px 0 10px 0;
	} */
`;

/* const ArticleDescription = styled.div`
	//width: 90%;
	max-height: 57px;
	min-height: 74px;
	overflow-wrap: break-word;
	overflow: hidden;
	font-size: 14px;
	line-height: 18px;
	text-overflow: ellipsis;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	margin-top: 10px;
	margin-right: 20px;

	@media (max-width: 900px) {
		width: 100%;
	}
`; */

const StyledSubdiv = styled.div`
	display: flex;
	font-weight: 300 !important;
	${({ theme }) => theme.textStyles.text};
	${gap(18, "row")}

	> div {
		display: flex;
		align-items: center;
		vertical-align: center;

		span {
			margin-top: 2px;
		}
	}

	svg {
		margin-right: 2px;
	}
	margin-top: 17px;
`;

const BottomInfos = styled.div`
	align-self: stretch;
	${row(10, "space-between")};
	max-height: 50px;
	min-height: 50px;
	margin-top: 10px;
	margin-bottom: 5px;
`;
const StyledUserView = styled(UserView)`
	color: ${({ theme }) => theme.colors.black};
`;
